import { Entity } from '@backstage/catalog-model';
import { catalogApiRef, EntityFilter, useEntityList } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { compact } from 'lodash';
import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  EntityInfraScopeFilter,
  InfraScopeKind,
} from './EntityInfraScopeFilter';

function matchesFilters(entity: Entity, filters: EntityFilter[]): boolean {
  return filters.every(
    filter => !filter.filterEntity || filter.filterEntity(entity),
  );
}

type UseInfraScopeCountsOptions = {
  username: string;
  isAdmin: boolean;
};

export function useInfraScopeCounts({
  username,
  isAdmin,
}: UseInfraScopeCountsOptions) {
  const catalogApi = useApi(catalogApiRef);
  const { filters } = useEntityList();

  const scopeFilters = useMemo(
    () => ({
      my: new EntityInfraScopeFilter('my', username, isAdmin),
      all: new EntityInfraScopeFilter('all', username, isAdmin),
    }),
    [username, isAdmin],
  );

  const baseFilters = useMemo(() => {
    const { user: _user, ...rest } = filters;
    return compact(Object.values(rest)) as EntityFilter[];
  }, [filters]);

  const kind = filters.kind?.value?.toLowerCase() ?? 'component';

  const { value: counts, loading } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind },
    });

    const matchingBase = items.filter(entity =>
      matchesFilters(entity, baseFilters),
    );

    return {
      my: matchingBase.filter(entity =>
        scopeFilters.my.filterEntity(entity),
      ).length,
      all: matchingBase.filter(entity =>
        scopeFilters.all.filterEntity(entity),
      ).length,
    } satisfies Record<InfraScopeKind, number>;
  }, [catalogApi, kind, baseFilters, scopeFilters]);

  return {
    counts: counts ?? { my: undefined, all: undefined },
    loading,
  };
}
